import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Package, Plus, RefreshCw, Copy, Check, ExternalLink, X, Trash2, FileText, Download, Lock } from 'lucide-react';
import apiClient from '../../api/apiClient';
import { Purchase, PURCHASE_STATUS_LABELS, ShowNotificationFn } from './crm.types';
import { CrmEmailSyncBanner } from './CrmEmailSyncBanner';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatPrice(n?: number): string {
  if (n == null) return '—';
  return n.toFixed(2) + ' €';
}

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

function monthKey(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key: string): string {
  const [y, m] = key.split('-');
  return `${MONTHS_FR[parseInt(m) - 1]} ${y}`;
}

// ── Modal factures ────────────────────────────────────────────────────────────

interface ReceiptsModalProps {
  /** Achats du tab courant seulement (payé, remboursé ou tous) */
  tabPurchases: Purchase[];
  /** IDs déjà cochés dans la liste principale (peut être vide) */
  preSelected: Set<number>;
  onClose: () => void;
  showNotification: ShowNotificationFn;
}

const ReceiptsModal: React.FC<ReceiptsModalProps> = ({ tabPurchases, preSelected, onClose, showNotification }) => {
  const hasPreSelected = preSelected.size > 0;

  const availableMonths = useMemo(() => {
    const keys = Array.from(new Set(tabPurchases.map(p => monthKey(p.created_at)).filter(Boolean)));
    return keys.sort((a, b) => b.localeCompare(a));
  }, [tabPurchases]);

  // Multi-sélection de mois (uniquement si pas de pré-sélection)
  const [selectedMonths, setSelectedMonths] = useState<Set<string>>(
    () => new Set(availableMonths.length > 0 ? [availableMonths[0]] : [])
  );

  // Items affichés : pré-sélectionnés directs OU filtrés par mois
  const displayedPurchases = useMemo(() => {
    if (hasPreSelected) return tabPurchases.filter(p => preSelected.has(p.id));
    return tabPurchases.filter(p => selectedMonths.has(monthKey(p.created_at)));
  }, [hasPreSelected, tabPurchases, preSelected, selectedMonths]);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => {
    if (hasPreSelected) return new Set(preSelected);
    const firstMonth = availableMonths[0] || '';
    return new Set(tabPurchases.filter(p => monthKey(p.created_at) === firstMonth).map(p => p.id));
  });

  const [loading, setLoading] = useState(false);

  const toggleMonth = (mk: string) => {
    const isSelected = selectedMonths.has(mk);
    if (isSelected && selectedMonths.size === 1) return; // garder au moins 1
    const nextMonths = new Set(selectedMonths);
    if (isSelected) {
      nextMonths.delete(mk);
      const toRemove = new Set(tabPurchases.filter(p => monthKey(p.created_at) === mk).map(p => p.id));
      setSelectedIds(prev => new Set(Array.from(prev).filter(id => !toRemove.has(id))));
    } else {
      nextMonths.add(mk);
      const toAdd = tabPurchases.filter(p => monthKey(p.created_at) === mk).map(p => p.id);
      setSelectedIds(prev => new Set([...Array.from(prev), ...toAdd]));
    }
    setSelectedMonths(nextMonths);
  };

  const toggleId = (id: number) =>
    setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const toggleAll = () => {
    if (selectedIds.size === displayedPurchases.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayedPurchases.map(p => p.id)));
    }
  };

  const handleGenerate = async (mode: 'open' | 'download') => {
    if (selectedIds.size === 0) { showNotification('Aucun achat sélectionné', '', 'error'); return; }
    setLoading(true);
    try {
      const blob = await apiClient.postBlob('/crm/purchases/receipts/merge', { purchase_ids: Array.from(selectedIds) });
      const url = URL.createObjectURL(blob);
      if (mode === 'open') {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = hasPreSelected
          ? 'factures_selection.pdf'
          : `factures_${Array.from(selectedMonths).sort().join('_')}.pdf`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
    } catch {
      showNotification('Erreur', 'Impossible de générer les factures', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-violet-600" />
            <h3 className="text-base font-bold text-gray-900">Éditer les factures</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><X size={20} /></button>
        </div>

        {/* Sélection des mois (masqué si pré-sélection) */}
        {!hasPreSelected && (
          <div className="px-6 pt-4 pb-3 flex-shrink-0">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Choisir un ou plusieurs mois
            </p>
            {availableMonths.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Aucun achat enregistré</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableMonths.map(mk => (
                  <button
                    key={mk}
                    onClick={() => toggleMonth(mk)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      selectedMonths.has(mk)
                        ? 'bg-violet-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {monthLabel(mk)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {hasPreSelected && (
          <div className="px-6 pt-4 pb-3 flex-shrink-0">
            <p className="text-xs text-gray-400 italic">{preSelected.size} article{preSelected.size > 1 ? 's' : ''} pré-sélectionné{preSelected.size > 1 ? 's' : ''}</p>
          </div>
        )}

        {/* Liste */}
        {displayedPurchases.length > 0 && (
          <>
            <div className="px-6 py-2 flex items-center justify-between flex-shrink-0 border-t border-gray-50">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={selectedIds.size === displayedPurchases.length && displayedPurchases.length > 0}
                  onChange={toggleAll}
                  className="w-4 h-4 rounded border-gray-300 accent-violet-600 cursor-pointer"
                />
                <span className="text-xs text-gray-500 font-medium">
                  {selectedIds.size > 0 ? `${selectedIds.size} sélectionné${selectedIds.size > 1 ? 's' : ''}` : 'Tout sélectionner'}
                </span>
              </label>
              <span className="text-xs text-gray-400">{displayedPurchases.length} achat{displayedPurchases.length > 1 ? 's' : ''}</span>
            </div>
            <div className="overflow-y-auto px-6 pb-2 flex-1 space-y-1.5">
              {displayedPurchases.map(p => (
                <label
                  key={p.id}
                  className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                    selectedIds.has(p.id) ? 'bg-violet-50 border border-violet-200' : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                  }`}
                >
                  <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleId(p.id)}
                    className="w-4 h-4 rounded border-gray-300 accent-violet-600 cursor-pointer flex-shrink-0" />
                  {p.item_image_url ? (
                    <img src={p.item_image_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                      <Package size={16} className="text-violet-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{p.item_title}</p>
                    <p className="text-xs text-gray-400">{formatDate(p.created_at)}</p>
                  </div>
                  <span className="text-sm font-bold text-violet-700 flex-shrink-0">{formatPrice(p.total_paid)}</span>
                </label>
              ))}
            </div>
          </>
        )}

        {displayedPurchases.length === 0 && (
          <div className="px-6 py-8 text-center text-gray-400 flex-1">
            <Package size={36} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Aucun achat pour la sélection</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center gap-2 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors">
            Annuler
          </button>
          <button onClick={() => handleGenerate('open')} disabled={loading || selectedIds.size === 0}
            className="flex items-center justify-center gap-1.5 flex-1 py-2.5 rounded-xl border border-violet-200 bg-violet-50 text-violet-700 text-sm font-semibold hover:bg-violet-100 disabled:opacity-40 transition-colors">
            <ExternalLink size={14} /> Ouvrir
          </button>
          <button onClick={() => handleGenerate('download')} disabled={loading || selectedIds.size === 0}
            className="flex items-center justify-center gap-1.5 flex-1 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40 transition-all shadow-sm">
            {loading ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
            {loading ? 'Génération…' : 'Télécharger'}
          </button>
        </div>

      </div>
    </div>
  );
};

// ── Modal ajout manuel ───────────────────────────────────────────────────────

interface AddPurchaseModalProps {
  onClose: () => void;
  onSaved: () => void;
  initialStatus?: string;
}

function calcProtectionFee(price: number): string {
  if (!price || price <= 0) return '0';
  const fee = price >= 500 ? price * 0.02 : 0.70 + price * 0.05;
  return fee.toFixed(2);
}

const AddPurchaseModal: React.FC<AddPurchaseModalProps> = ({ onClose, onSaved, initialStatus = 'paid' }) => {
  const [form, setForm] = useState({
    item_title: '', item_size: '', item_brand: '', item_condition: '',
    price_paid: '', shipping_cost: '0', protection_fee: '0',
    seller_name: '', tracking_number: '', transaction_id: '', notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handlePriceChange = (val: string) => {
    const price = parseFloat(val) || 0;
    setForm(f => ({ ...f, price_paid: val, protection_fee: calcProtectionFee(price) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.item_title || !form.price_paid) { setError('Titre et prix obligatoires'); return; }
    setLoading(true);
    try {
      const price = parseFloat(form.price_paid) || 0;
      const ship  = parseFloat(form.shipping_cost) || 0;
      const prot  = parseFloat(form.protection_fee) || 0;
      await apiClient.post('/crm/purchases', {
        ...form,
        price_paid: price, shipping_cost: ship, protection_fee: prot,
        total_paid: price + ship + prot,
        status: initialStatus,
      });
      onSaved();
      onClose();
    } catch {
      setError('Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">Ajouter un achat</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre de l'article *</label>
            <input type="text" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={form.item_title} onChange={e => setForm(f => ({ ...f, item_title: e.target.value }))} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix *</label>
              <input type="number" step="0.01" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                value={form.price_paid} onChange={e => handlePriceChange(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frais de port</label>
              <input type="number" step="0.01" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                value={form.shipping_cost} onChange={e => setForm(f => ({ ...f, shipping_cost: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Protection acheteur</label>
              <input type="number" step="0.01" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                value={form.protection_fee} onChange={e => setForm(f => ({ ...f, protection_fee: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[['item_size','Taille','M'],['item_brand','Marque','Zara'],['item_condition','État','Bon état']].map(([k,l,ph]) => (
              <div key={k}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{l}</label>
                <input type="text" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  value={(form as any)[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} placeholder={ph} />
              </div>
            ))}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendeur</label>
            <input type="text" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={form.seller_name} onChange={e => setForm(f => ({ ...f, seller_name: e.target.value }))} placeholder="Pseudo Vinted" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de suivi <span className="text-gray-400 font-normal">(transport)</span></label>
              <input type="text" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                value={form.tracking_number} onChange={e => setForm(f => ({ ...f, tracking_number: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de transaction</label>
              <input type="text" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                value={form.transaction_id} onChange={e => setForm(f => ({ ...f, transaction_id: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Carte achat ───────────────────────────────────────────────────────────────

interface PurchaseCardProps {
  purchase: Purchase;
  selected: boolean;
  onToggleSelect: (id: number) => void;
}

const PurchaseCard: React.FC<PurchaseCardProps> = ({ purchase, selected, onToggleSelect }) => {
  const [copied, setCopied] = useState(false);

  const statusInfo = PURCHASE_STATUS_LABELS[purchase.status] || { label: purchase.status, color: 'text-gray-600', bg: 'bg-gray-100' };
  const isRefunded = purchase.status === 'refunded';

  const copyCode = () => {
    if (purchase.pickup_code) {
      navigator.clipboard.writeText(purchase.pickup_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={`bg-white border rounded-xl shadow-sm transition-all ${selected ? 'border-violet-400 ring-1 ring-violet-200' : 'border-gray-100'}`}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-2 px-3 pt-2.5 pb-2">
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(purchase.id)}
          className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-violet-600 cursor-pointer flex-shrink-0"
        />
        {/* Image */}
        {purchase.item_image_url ? (
          <img src={purchase.item_image_url} alt="" className="w-11 h-11 rounded-lg object-cover flex-shrink-0" />
        ) : (
          <div className="w-11 h-11 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
            <Package size={18} className="text-violet-400" />
          </div>
        )}

        {/* Titre + vendeur + prix */}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-[13px] leading-tight truncate">{purchase.item_title}</p>
          {purchase.seller_name && (
            <div className="flex items-center gap-1 mt-0">
              <span className="text-xs text-gray-400">👤</span>
              {purchase.seller_url ? (
                <a href={purchase.seller_url} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-violet-600 hover:underline flex items-center gap-0.5">
                  {purchase.seller_name} <ExternalLink size={10} />
                </a>
              ) : (
                <span className="text-xs text-gray-500">{purchase.seller_name}</span>
              )}
            </div>
          )}
          <span className="font-bold text-violet-700 text-xs mt-0.5 block">{formatPrice(purchase.total_paid)}</span>
        </div>

        {/* Statut + date */}
        <div className="flex flex-col items-end flex-shrink-0 gap-0.5">
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${statusInfo.bg} ${statusInfo.color}`}>
            {statusInfo.label}
          </span>
          {isRefunded && purchase.inventory_after_refund && (
            <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full">Stock</span>
          )}
          <span className="text-[10px] text-gray-400">{formatDate(purchase.created_at)}</span>
        </div>
      </div>

      {/* ── Banderole prix ──────────────────────────────────────────────── */}
      <div className="border-t border-gray-100 px-3 py-1.5">
        <div className="flex items-center bg-gray-50 rounded-xl px-2 py-1">
          <div className="flex-1 text-center">
            <p className="text-[9px] uppercase tracking-wide text-gray-400">Article</p>
            <p className="text-xs font-semibold text-gray-800">{formatPrice(purchase.price_paid)}</p>
          </div>
          <span className="text-gray-300 text-xs">+</span>
          <div className="flex-1 text-center">
            <p className="text-[9px] uppercase tracking-wide text-gray-400">Livraison</p>
            <p className="text-xs font-semibold text-gray-800">{formatPrice(purchase.shipping_cost)}</p>
          </div>
          <span className="text-gray-300 text-xs">+</span>
          <div className="flex-1 text-center">
            <p className="text-[9px] uppercase tracking-wide text-gray-400">Protection</p>
            <p className="text-xs font-semibold text-gray-800">{formatPrice(purchase.protection_fee)}</p>
          </div>
          <span className="text-gray-300 text-xs">=</span>
          <div className="flex-1 text-center">
            <p className="text-[9px] uppercase tracking-wide text-gray-400">Total</p>
            <p className="text-xs font-bold text-violet-700">{formatPrice(purchase.total_paid)}</p>
          </div>
        </div>
      </div>

      {/* ── Infos supplémentaires (pickup, tracking) ────────────────────── */}
      {(purchase.pickup_point_name || purchase.tracking_number || purchase.carrier) && (
        <div className="border-t border-gray-50 flex flex-wrap items-center px-3 py-1.5 gap-2">
          {purchase.pickup_point_name && (
            <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
              📍 {purchase.pickup_point_name}
            </span>
          )}
          {purchase.pickup_code && (
            <button onClick={copyCode} className="flex items-center gap-1 text-xs text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full hover:bg-violet-100 transition-colors">
              {copied ? <Check size={11} /> : <Copy size={11} />}
              {copied ? 'Copié' : purchase.pickup_code}
            </button>
          )}
          {purchase.carrier && (
            <span className="text-xs text-gray-500">📦 {purchase.carrier}</span>
          )}
          {purchase.tracking_number && (
            <span className="text-xs text-gray-400 font-mono">{purchase.tracking_number}</span>
          )}
        </div>
      )}
    </div>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────

interface CrmAchatsProps {
  showNotification: ShowNotificationFn;
  crm_email_active?: boolean;
  is_free_plan?: boolean;
  onUpgrade?: () => void;
}

type TabKey = 'paid' | 'refunded' | 'all';

const CrmAchats: React.FC<CrmAchatsProps> = ({ showNotification, crm_email_active, is_free_plan, onUpgrade }) => {
  const [purchases, setPurchases]       = useState<Purchase[]>([]);
  const [loading, setLoading]           = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReceipts, setShowReceipts] = useState(false);
  const [activeTab, setActiveTab]       = useState<TabKey>('paid');
  const [selected, setSelected]         = useState<Set<number>>(new Set());

  const loadPurchases = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/crm/purchases');
      setPurchases(res.data);
    } catch {
      showNotification('Erreur', 'Impossible de charger les achats', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { loadPurchases(); }, [loadPurchases]);

  const handleBulkMoveToStock = async () => {
    if (selected.size === 0) { showNotification('Aucun achat sélectionné', '', 'error'); return; }
    const ids = Array.from(selected);
    const n = ids.length;
    try {
      await Promise.all(ids.map(id => apiClient.patch(`/crm/purchases/${id}`, { move_to_stock: true })));
      setSelected(new Set());
      showNotification(`${n} article${n > 1 ? 's' : ''} ajouté${n > 1 ? 's' : ''} au stock`, '', 'success');
      loadPurchases();
    } catch {
      showNotification('Erreur', 'Impossible de passer au stock', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) {
      showNotification('Aucun achat sélectionné', 'Cochez des achats à supprimer', 'error');
      return;
    }
    if (!window.confirm(`Supprimer ${selected.size} achat${selected.size > 1 ? 's' : ''} ?`)) return;
    try {
      await Promise.all(Array.from(selected).map(id => apiClient.delete(`/crm/purchases/${id}`)));
      setPurchases(prev => prev.filter(p => !selected.has(p.id)));
      setSelected(new Set());
      showNotification(`${selected.size} achat${selected.size > 1 ? 's' : ''} supprimé${selected.size > 1 ? 's' : ''}`, '', 'success');
    } catch {
      showNotification('Erreur', 'Impossible de supprimer', 'error');
    }
  };

  const toggleSelect = (id: number) =>
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const toggleSelectAll = () => {
    if (selected.size === displayed.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(displayed.map(p => p.id)));
    }
  };

  const paid     = purchases.filter(p => p.status !== 'refunded');
  const refunded = purchases.filter(p => p.status === 'refunded');
  const displayed = activeTab === 'all' ? purchases : activeTab === 'paid' ? paid : refunded;

  const TABS: { key: TabKey; label: string; count: number }[] = [
    { key: 'paid',     label: 'Payé',      count: paid.length },
    { key: 'refunded', label: 'Remboursé', count: refunded.length },
    { key: 'all',      label: 'Toutes',    count: purchases.length },
  ];

  return (
    <div className="flex flex-col h-full bg-gray-50">

      {/* ── Header fixe — ne scroll pas ─────────────────────────────────── */}
      <div className="flex-shrink-0 bg-gray-50 px-6 pt-6 pb-3">

        {/* Titre + boutons Actualiser / Ajouter */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 flex-wrap">
              <Package size={24} className="text-violet-600" /> Achats
            </h1>
            <p className="text-gray-500 text-sm mt-0.5 flex items-center">
              {purchases.length} achat{purchases.length !== 1 ? 's' : ''} enregistré{purchases.length !== 1 ? 's' : ''}
              {is_free_plan && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ml-2 ${purchases.length >= 10 ? 'bg-red-100 text-red-600' : 'bg-violet-100 text-violet-700'}`}>
                  {Math.min(purchases.length, 10)} / 10
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto min-w-0 max-w-xl">
            {!crm_email_active && <CrmEmailSyncBanner />}
            <div className="flex items-center justify-end gap-2 flex-shrink-0">
              <button onClick={loadPurchases} className="p-2 rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 transition-all">
                <RefreshCw size={16} />
              </button>
              <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-semibold shadow hover:opacity-90 transition-all">
                <Plus size={16} /> Ajouter
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-200 p-1 rounded-xl w-fit mb-3">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSelected(new Set()); }}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.key ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                  activeTab === tab.key ? 'bg-violet-100 text-violet-700' : 'bg-gray-300 text-gray-500'
                }`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Barre tout sélectionner + boutons d'action */}
        {displayed.length > 0 && (
          <div className="flex items-center gap-3 px-1">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={selected.size === displayed.length && displayed.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-gray-300 accent-violet-600 cursor-pointer"
              />
              <span className="text-xs text-gray-500 font-medium">
                {selected.size > 0 ? `${selected.size} sélectionné${selected.size > 1 ? 's' : ''}` : 'Tout sélectionner'}
              </span>
            </label>
            <div className="flex gap-2 ml-auto">
              {activeTab === 'paid' && (
                <button onClick={() => setShowReceipts(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-50 border border-violet-200 text-violet-700 text-xs font-semibold hover:bg-violet-100 transition-colors">
                  <FileText size={13} /> Factures
                </button>
              )}
              {activeTab === 'refunded' && (
                <button onClick={handleBulkMoveToStock}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition-colors">
                  <Package size={13} /> Passage en stock
                </button>
              )}
              <button onClick={handleBulkDelete}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-100 transition-colors">
                <Trash2 size={13} /> Supprimer
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Liste scrollable ────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-6 pb-6 pt-3">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={24} className="animate-spin text-violet-400" />
          </div>
        ) : displayed.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <Package size={48} className="mx-auto mb-3 opacity-30" />
            <p className="text-base font-medium">
              {activeTab === 'refunded' ? 'Aucun achat remboursé' : 'Aucun achat enregistré'}
            </p>
            <p className="text-sm mt-1">Configure le transfert d'emails pour recevoir tes achats automatiquement</p>
          </div>
        ) : (
          <div className="space-y-2">
            {is_free_plan && displayed.length > 10 && (
              <div className="relative mb-2">
                <div className="blur-sm pointer-events-none select-none space-y-2">
                  {displayed.slice(0, displayed.length - 10).map(p => (
                    <PurchaseCard key={p.id} purchase={p} selected={false} onToggleSelect={() => {}} />
                  ))}
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 text-center shadow-xl border border-gray-100 mx-4 max-w-xs">
                    <div className="w-12 h-12 rounded-full bg-violet-50 flex items-center justify-center mx-auto mb-3">
                      <Lock size={22} className="text-violet-500" />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-1">Limite gratuite atteinte</h4>
                    <p className="text-xs text-gray-500 mb-4">Passez à Starter pour un CRM illimité.</p>
                    <button onClick={onUpgrade} className="px-5 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white text-sm font-semibold hover:opacity-90 transition-all shadow">
                      Voir les plans →
                    </button>
                  </div>
                </div>
              </div>
            )}
            {(is_free_plan && displayed.length > 10 ? displayed.slice(displayed.length - 10) : displayed).map(p => (
              <PurchaseCard
                key={p.id}
                purchase={p}
                selected={selected.has(p.id)}
                onToggleSelect={toggleSelect}
              />
            ))}
          </div>
        )}
      </div>

      {showAddModal && <AddPurchaseModal onClose={() => setShowAddModal(false)} onSaved={loadPurchases} initialStatus={activeTab === 'all' ? 'paid' : activeTab} />}
      {showReceipts && (
        <ReceiptsModal
          tabPurchases={displayed}
          preSelected={selected}
          onClose={() => setShowReceipts(false)}
          showNotification={showNotification}
        />
      )}
    </div>
  );
};

export default CrmAchats;
